import { AxiosError } from 'axios'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { Subscription, distinctUntilChanged } from 'rxjs'
import { func, validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { SearchableUser } from 'iam/models/SearchableUser'
import Team from 'iam/models/Team'
import TeamMember from 'iam/models/TeamMember'
import TeamRole from 'iam/models/TeamRole'
import Workspace from 'iam/models/Workspace'
import TeamStore from 'iam/TeamStore'
import TeamMemberStore from 'iam/TeamMemberStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import ModalViewModel from 'infra/ModalViewModel'
import { Id, Nullable } from 'types/definitions'
import { tapAction, toStream } from 'utils/mobx-util'

export type ShowForWorkspace = (workspace: Workspace) => void

interface AddTeamMemberResult {
  teamMember?: TeamMember
  success?: boolean
  error?: AxiosError
}

export class AddUserToTeamViewModel extends ModalViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * The currently selected workspace.
   */
  @observable
  workspace: Nullable<Workspace> = null

  /**
   * The currently selected team.
   */
  @observable
  team: Nullable<Team> = null

  /**
   * The currently selected team role.
   */
  @observable
  teamRole: Nullable<TeamRole> = null

  /**
   * The list of teams available in the selected workspace.
   */
  @observable
  teams: Team[] = []

  /**
   * The list of team roles available within the selected team.
   */
  @observable
  teamRoles: TeamRole[] = []

  /**
   * The list of existing team members for the selected user.
   */
  @observable
  existingTeamMembers: TeamMember[] = []

  /**
   * Validation rules for adding a user to a team.
   */
  validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    team: [func(() => this.team !== null, 'You must select a team.')],
    teamRole: [func(() => this.teamRole !== null, 'You must select a role.')],
  })

  /**
   * Stream of changes to the add user to team form inputs.
   */
  private addUserToTeamChanges$ = toStream(() => this.workspace, true).pipe(
    distinctUntilChanged(),
    tapAction((workspace) => {
      this.clearSelections()

      if (workspace) {
        this.updateOptions()

        this.fetchExistingTeamMembers()
      }
    }),
  )

  constructor(
    private _user: () => SearchableUser,
    private newAlert: AlertFactory,
    private teamStore: TeamStore,
    private teamMemberStore: TeamMemberStore,
    private teamRoleStore: TeamRoleStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Activate the view model.
   */
  activate() {
    this.subscription = this.addUserToTeamChanges$.subscribe()
  }

  /**
   * Deactivate the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
    this.reset()
  }

  /**
   * The currently selected user.
   */
  @computed
  get user() {
    return this._user()
  }

  /**
   * The list of workspaces the current user (i.e. the admin) may create team members in.
   */
  @computed
  get currentUserWorkspaces() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.hasPermission('ADMIN_MANAGE_TEAM_MEMBER'))
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces available to the currently selected user (i.e. the one being viewed).
   */
  @computed
  get selectedUserWorkspaces() {
    return this.user.memberships.map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces that are available to the currently selected user
   * AND that the current user may create team members in.
   */
  @computed
  get availableWorkspaces() {
    return this.selectedUserWorkspaces.filter((ws) =>
      this.currentUserWorkspaces.includes(ws),
    )
  }

  /**
   * The list of teams that are available to be joined by the currently selected user
   * (i.e. teams that the currently selected user is NOT already a member of).
   */
  @computed
  get availableTeams() {
    return this.teams.filter(
      (team) =>
        !this.existingTeamMembers.some(
          (teamMember) => teamMember.team.id === team.id,
        ),
    )
  }

  /**
   * Set the currently selected workspace.
   */
  @action.bound
  setWorkspace(workspace: Nullable<Workspace>) {
    this.workspace = workspace
  }

  /**
   * Set the currently selected team.
   */
  @action.bound
  setTeam(team: Nullable<Team>) {
    this.team = team
  }

  /**
   * Set the currently selected team role.
   */
  @action.bound
  setTeamRole(teamRole: Nullable<TeamRole>) {
    this.teamRole = teamRole
  }

  /**
   * Set the list of teams available in the selected workspace.
   */
  @action.bound
  setTeams(teams: Team[]) {
    this.teams = teams
  }

  /**
   * Set the list of team roles available within the selected team.
   */
  @action.bound
  setTeamRoles(teamRoles: TeamRole[]) {
    this.teamRoles = teamRoles
  }

  /**
   * Set the list of existing team members for the selected user.
   */
  @action.bound
  setExistingTeamMembers(teamMembers: TeamMember[]) {
    this.existingTeamMembers = teamMembers
  }

  /**
   * Update the list of teams and team roles based on the selected workspace.
   */
  @action.bound
  async updateOptions() {
    if (!this.workspace) {
      return
    }

    await this.teamStore.findTeams({
      workspace_id: Id.assert(this.workspace.id),
    })
    await this.teamRoleStore.find({
      workspace_id: Id.assert(this.workspace.id),
    })

    this.setTeams(
      this.teamStore.teams.items.filter(
        // We know that workspace exists at this point.
        (t) => t.workspace.id === this.workspace!.id,
      ),
    )
    this.setTeamRoles(
      this.teamRoleStore.forWorkspace(Id.assert(this.workspace.id)),
    )
  }

  /**
   * Fetch the list of existing team members for the selected user.
   */
  @action.bound
  async fetchExistingTeamMembers() {
    if (!this.workspace) {
      return
    }

    const teamMembers = await this.teamMemberStore.search({
      user_public_id: Id.assert(this.user.publicId),
      workspace_id: Id.assert(this.workspace.id),
    })

    this.setExistingTeamMembers(teamMembers)
  }

  /**
   * Clear relevant selections when a new workspace is selected.
   */
  @action.bound
  clearSelections() {
    this.setTeam(null)
    this.setTeamRole(null)

    this.setExistingTeamMembers([])
  }

  /**
   * Reset the view model to its default state.
   */
  @action.bound
  reset() {
    this.setWorkspace(null)
    this.setTeam(null)
    this.setTeamRole(null)

    this.setTeams([])
    this.setTeamRoles([])

    this.setExistingTeamMembers([])

    this.validation.reset()
  }

  /**
   * Add the currently selected user to the selected team with the specified team role.
   */
  @action.bound
  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const result: AddTeamMemberResult = await this.teamMemberStore
      .addTeamMemberByPublicId({
        // Although param name is user id, value must be public id.
        user_id: this.user.publicId,
        team_id: Id.assert(this.team?.id),
        team_role_id: Id.assert(this.teamRole?.id),
        workspace_id: Id.assert(this.workspace?.id),
      })
      .then((result) => {
        return { teamMember: result, success: true }
      })
      .catch((err) => {
        return { error: err }
      })

    if (result.success) {
      const displayName = result.teamMember?.member.displayName
      const teamName = result.teamMember?.team.name
      const teamRoleName = result.teamMember?.teamRole.name

      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Added ${displayName} to ${teamName} with role ${teamRoleName}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
    } else if (result.error) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Failed to add user to team.', `${result.error.message}`)
        .autoDismiss(2500)
        .show()
    }
  })

  /**
   * Open the dialog and activate the view model.
   */
  @override
  show() {
    this.activate()
    super.show()
  }

  /**
   * Open the dialog and activate the view model with the specified workspace.
   */
  showForWorkspace: ShowForWorkspace = (workspace: Workspace) => {
    this.setWorkspace(workspace)
    this.show()
  }

  /**
   * Close the dialog and reset selections to default.
   */
  @override
  close() {
    super.close()
    this.deactivate()
  }
}
